import { OwnershipStatusFields } from '@/lib/components/core';
import { useReviewEnvironmentReport } from '@/lib/queries/reports';
import { OwnershipType, hasOwnershipStatus } from '@/lib/types';
import { TeeltplanIndex } from '@/lib/types/domain';
import { ReviewType } from '@/lib/types/reports';
import { FormOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Modal, Radio } from 'antd';
import type { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ReviewEnvReportButtonProps {
  reportData?: TeeltplanIndex;
  onOk: () => void;
}

export const ReviewEnvReportButton: React.FunctionComponent<ReviewEnvReportButtonProps> = ({ reportData, onOk }) => {
  const { t } = useTranslation('reports');
  const reviewEnvReport = useReviewEnvironmentReport();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewType | null>(null);
  const [form] = Form.useForm();
  const showOwnershipStatus = hasOwnershipStatus(reportData?.registratieJaar);

  const initialValues = {
    reason: reportData?.review?.reason,
    dateOfReview: reportData?.review?.dateOfReview,
    reviewStatusId: reportData?.review?.status,
    reviewStatusName: reportData?.review?.reviewStatusName,
    eigendomstype_bevoegd_gezag: reportData?.review?.eigendomstype_bevoegd_gezag ?? reportData?.review?.eigendomstype,
    eigendomstype_bevoegd_gezag_beginDatum:
      reportData?.review?.eigendomstype_bevoegd_gezag_beginDatum ?? reportData?.review?.eigendomstypeBegindatum,
    eigendomstype_bevoegd_gezag_eindDatum:
      reportData?.review?.eigendomstype_bevoegd_gezag_eindDatum ?? reportData?.review?.eigendomstypeEinddatum,
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    if (reviewType === null) {
      return;
    }

    const values = (await form.validateFields()) as {
      reason?: string;
      reviewStatusId: number;
      reviewStatusName?: string;
      datumBedrijfsbezoek?: Dayjs | string;
      eigendomstype_bevoegd_gezag?: OwnershipType;
      eigendomstype_bevoegd_gezag_beginDatum?: string;
      eigendomstype_bevoegd_gezag_eindDatum?: string;
    };

    const { eigendomstype_bevoegd_gezag, eigendomstype_bevoegd_gezag_beginDatum, eigendomstype_bevoegd_gezag_eindDatum, ...payloadValues } =
      values;

    const reviewPayload = {
      ...payloadValues,
      teeltplanId: reportData?.id,
      datumVanVerwerking: new Date(),
      type: reviewType,
      datumBedrijfsbezoek:
        typeof values.datumBedrijfsbezoek === 'string'
          ? values.datumBedrijfsbezoek
          : values.datumBedrijfsbezoek?.format('YYYY-MM-DD') ?? null,
      ...(showOwnershipStatus
        ? {
            eigendomstype: eigendomstype_bevoegd_gezag ?? null,
            eigendomstypeBegindatum: eigendomstype_bevoegd_gezag_beginDatum ?? null,
            eigendomstypeEinddatum: eigendomstype_bevoegd_gezag_eindDatum ?? null,
          }
        : {}),
    };

    reviewEnvReport.mutate(reviewPayload, {
      onSuccess: () => {
        setIsModalVisible(false);
        setReviewType(null);
        form.resetFields();
        onOk();
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setReviewType(null);
    setIsModalVisible(false);
  };

  const handleSelectReviewType = (type: ReviewType | null) => {
    setReviewType(type);
  };

  useEffect(() => {
    if (reviewType === ReviewType.Bureaucontrole) {
      form.setFieldsValue({
        eigendomstype_bevoegd_gezag: OwnershipType.NotReviewed,
        eigendomstype_bevoegd_gezag_beginDatum: undefined,
        eigendomstype_bevoegd_gezag_eindDatum: undefined,
      });
    }
  }, [form, reviewType]);

  const handleBack = () => {
    form.resetFields();
    setReviewType(null);
  };

  const renderTypeSelection = () => (
    <>
      <p style={{ marginBottom: '16px' }}>Kies type keuring</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Button style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }} onClick={() => handleSelectReviewType(ReviewType.Bureaucontrole)}>
          Bureaucontrole
        </Button>
        <Button style={{ textAlign: 'left', height: 'auto', padding: '12px 16px' }} onClick={() => handleSelectReviewType(ReviewType.Bedrijfsbezoek)}>
          Bedrijfsbezoek
        </Button>
      </div>
    </>
  );

  const getOwnershipOptions = () => [
    {
      label: t('reviewModal.ownership.option_0'),
      value: OwnershipType.Unknown,
    },
    {
      label: t('reviewModal.ownership.option_1'),
      value: OwnershipType.OwnCompany,
    },
    {
      label: t('reviewModal.ownership.option_2'),
      value: OwnershipType.GroupCompany,
    },
    {
      label: t('reviewModal.ownership.option_3'),
      value: OwnershipType.ThirdPartyLease,
    },
    ...(reviewType === ReviewType.Bureaucontrole
      ? [
          {
            label: t('reviewModal.ownership.option_4'),
            value: OwnershipType.NotReviewed,
          },
        ]
      : []),
  ];

  const renderReviewForm = () => (
    <>
      <div style={{ paddingBottom: '20px' }}>
        <p>Keur het milieurapport door gebruik te maken van onderstaande opties en geef extra toelichting wanneer nodig.</p>
      </div>
      <Form form={form} name="review" layout="vertical" initialValues={initialValues}>
        <Form.Item name="reviewStatusId" rules={[{ required: true, message: 'Selecteer een optie' }]} style={{ textAlign: 'center' }}>
          <Radio.Group>
            <Radio.Button value={2} style={{ margin: '5px' }}>
              Goedkeuren
            </Radio.Button>
            <Radio.Button value={3} style={{ margin: '5px' }}>
              Afkeuren
            </Radio.Button>
            <Radio.Button value={1} style={{ margin: '5px' }}>
              Onvolledig
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
        {reviewType === ReviewType.Bedrijfsbezoek && (
          <Form.Item name="datumBedrijfsbezoek" label="Datum bedrijfsbezoek" rules={[{ required: true, message: 'Datum bedrijfsbezoek is verplicht' }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="Selecteer datum" />
          </Form.Item>
        )}
        {showOwnershipStatus && (
          <OwnershipStatusFields
            form={form}
            helpText={t('reviewModal.ownership.helpText')}
            options={getOwnershipOptions()}
            question={t('reviewModal.ownership.question')}
            required={reviewType === ReviewType.Bedrijfsbezoek}
            requiredMessage={t('reviewModal.ownership.required')}
            startDateFieldName="eigendomstype_bevoegd_gezag_beginDatum"
            endDateFieldName="eigendomstype_bevoegd_gezag_eindDatum"
            typeFieldName="eigendomstype_bevoegd_gezag"
          />
        )}
        <Form.Item name="reason" rules={[{ required: false }]}>
          <Input type="text" placeholder="Toelichting (niet verplicht)" />
        </Form.Item>
      </Form>
    </>
  );

  const getModalTitle = () => {
    if (reviewType === null) {
      return 'Milieurapport keuren';
    }
    return reviewType === ReviewType.Bureaucontrole ? 'Milieurapport keuren - Bureaucontrole' : 'Milieurapport keuren - Bedrijfsbezoek';
  };

  return (
    <>
      <Button icon={<FormOutlined />} onClick={showModal} title="Milierapport keuren" />
      <Modal
        title={getModalTitle()}
        open={isModalVisible}
        onOk={reviewType === null ? undefined : handleOk}
        onCancel={handleCancel}
        okButtonProps={{ style: { display: reviewType === null ? 'none' : undefined } }}
        footer={(_, { OkBtn }) => (
          <>
            {reviewType !== null && <Button onClick={handleBack}>Terug</Button>}
            {reviewType !== null && <OkBtn />}
          </>
        )}>
        {reviewType === null ? renderTypeSelection() : renderReviewForm()}
      </Modal>
    </>
  );
};
